import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import Prism from 'prismjs';

import '../../prism/prism.css';

const BorderDiv = styled.div`
  transition: all 150ms ease-in-out;
  border-bottom-left-radius: ${props =>
    props.borderBottomLeft
      ? props.borderBottomLeft + props.unit
      : 0 + props.unit};
  border-bottom-right-radius: ${props =>
    props.borderBottomRight
      ? props.borderBottomRight + props.unit
      : 0 + props.unit};
  border-top-left-radius: ${props =>
    props.borderTopLeft ? props.borderTopLeft + props.unit : 0 + props.unit};
  border-top-right-radius: ${props =>
    props.borderTopRight ? props.borderTopRight + props.unit : 0 + props.unit};
`;

const ButtonUnit = ({ children, active = false, left = false, ...props }) => {
  const base = `font-bold py-2 px-4 rounded-${left ? 'l' : 'r'}`;

  let fluid = 'bg-gray-200 hover:bg-gray-400 shadow text-gray-800';
  if (active) fluid = 'bg-green-600 hover:bg-green-400 shadow-inner text-white';

  return (
    <button {...props} className={base + ' ' + fluid}>
      {children}
    </button>
  );
};

const inputClassName =
  'absolute w-20 h-12 text-xl pl-4 shadow-lg rounded text-white bg-gray-900 border-purple-800 border-b-4';

const Border = ({ dark }) => {
  const [borderTopRight, setBorderTopRight] = useState(100);
  const [borderBottomRight, setBorderBottomRight] = useState(100);
  const [borderTopLeft, setBorderTopLeft] = useState(4);
  const [borderBottomLeft, setBorderBottomLeft] = useState(100);

  const [unit, setUnit] = useState('%');

  const [showCode, setShowCode] = useState(false);

  const [borderCode, setBorderCode] = useState(
    `${borderTopLeft}${unit} ${borderTopRight}${unit} ${borderBottomRight}${unit} ${borderBottomLeft}${unit}`
  );

  useEffect(() => {
    if (unit === '%') {
      if (borderTopRight > 100) setBorderTopRight(100);
      if (borderBottomRight > 100) setBorderBottomRight(100);
      if (borderTopLeft > 100) setBorderTopLeft(100);
      if (borderBottomLeft > 100) setBorderBottomLeft(100);
    }

    if (borderTopRight < 0) setBorderTopRight(0);
    if (borderBottomRight < 0) setBorderBottomRight(0);
    if (borderTopLeft < 0) setBorderTopLeft(0);
    if (borderBottomLeft < 0) setBorderBottomLeft(0);

    setBorderCode(
      `${borderTopLeft}${unit} ${borderTopRight}${unit} ${borderBottomRight}${unit} ${borderBottomLeft}${unit}`
    );

    if (borderTopRight === borderBottomLeft)
      setBorderCode(
        `${borderTopLeft}${unit} ${borderTopRight}${unit} ${borderBottomRight}${unit}`
      );

    if (
      borderTopLeft === borderBottomRight &&
      borderTopRight === borderBottomLeft
    )
      setBorderCode(`${borderTopLeft}${unit} ${borderTopRight}${unit}`);

    if (
      borderTopLeft === borderBottomRight &&
      borderTopRight === borderBottomLeft &&
      borderBottomLeft === borderBottomRight
    )
      setBorderCode(`${borderTopLeft}${unit}`);
  }, [
    borderTopRight,
    borderBottomRight,
    borderTopLeft,
    borderBottomLeft,
    unit
  ]);
  useEffect(() => {
    if (showCode) setTimeout(() => Prism.highlightAll(), 0);
  }, [
    borderTopRight,
    borderBottomRight,
    borderTopLeft,
    borderBottomLeft,
    showCode,
    unit
  ]);

  const [copySuccess, setCopySuccess] = useState(false);
  const cssCode = useRef(null);

  const copyText = element => {
    let range, selection;

    if (document.body.createTextRange) {
      range = document.body.createTextRange();
      range.moveToElementText(element);
      range.select();
    } else if (window.getSelection) {
      selection = window.getSelection();
      range = document.createRange();
      range.selectNodeContents(element);
      selection.removeAllRanges();
      selection.addRange(range);
    }

    try {
      document.execCommand('copy');
    } catch (err) {
      console.error('err :', err);
      alert('unable to copy text');
    }
  };

  const copyCode = e => {
    copyText(cssCode.current);

    e.target.focus();

    setCopySuccess(true);
    setShowCode(false);
  };

  useEffect(() => {
    if (copySuccess) setTimeout(() => setCopySuccess(!copySuccess), 3000);
  }, [copySuccess]);

  return (
    <section class="flex-1 flex flex-col xl:mt-4 mb-10 text-center items-center h-full" style={{marginTop: window.innerWidth > 770 ? "3%" : "12%"}}>
      <h1
        className={`text-5xl w-full font-bold tracking-wider  select-none ${
          dark ? 'text-white' : 'text-indigo-900'
        }`}
      >
        Border Radius
      </h1>

      <BorderDiv
        borderBottomLeft={borderBottomLeft}
        borderBottomRight={borderBottomRight}
        borderTopLeft={borderTopLeft}
        borderTopRight={borderTopRight}
        unit={unit}
        className="w-56 h-56 mt-20 block relative border border-indigo-500 bg-indigo-600 shadow-md"
      >
        <input
          className={inputClassName}
          style={{
            top: '-30%',
            left: '-40%'
          }}
          type="number"
          value={borderTopLeft}
          onChange={e => setBorderTopLeft(e.target.value)}
        />

        <input
          className={inputClassName}
          style={{
            top: '-30%',
            right: '-40%'
          }}
          type="number"
          value={borderTopRight}
          onChange={e => setBorderTopRight(e.target.value)}
        />
        <input
          className={inputClassName}
          style={{
            bottom: '-30%',
            left: '-40%'
          }}
          type="number"
          value={borderBottomLeft}
          onChange={e => setBorderBottomLeft(e.target.value)}
        />
        <input
          className={inputClassName}
          style={{
            bottom: '-30%',
            right: '-40%'
          }}
          type="number"
          value={borderBottomRight}
          onChange={e => setBorderBottomRight(e.target.value)}
        />

        <div className="w-full h-full flex items-center justify-center">
          <div className="inline-flex">
            <ButtonUnit
              active={unit === 'px' ? true : false}
              onClick={() => setUnit('px')}
              left={true}
            >
              px
            </ButtonUnit>
            <ButtonUnit
              active={unit === '%' ? true : false}
              onClick={() => setUnit('%')}
            >
              %
            </ButtonUnit>
          </div>
        </div>
      </BorderDiv>

      {!showCode && (
        <button
          className={`mt-40 shadow text-white font-bold py-2 px-4 rounded ${
            dark
              ? 'bg-pink-600 hover:bg-pink-700'
              : 'bg-pink-700 hover:bg-pink-600'
          }`}
          onClick={() => setShowCode(!showCode)}
        >
          Get CSS Code
        </button>
      )}

      {showCode && (
        <div
          className="fixed"
          style={{
            bottom: '18%'
          }}
        >
          <pre className=" shadow-md bottom-0 language-css">
            <span
              onClick={() => setShowCode(!showCode)}
              className="absolute right-0 top-0 px-3 py-4 rounded cursor-pointer select-none focus:bg-blue-200"
            >
              &#10006;
            </span>
            <code ref={cssCode}>
              {`.borderRounded {
  -webkit-border-radius: ${borderCode}; 
  -moz-border-radius: ${borderCode}; 
  border-radius: ${borderCode}; 
}`}
            </code>
          </pre>
          <button
            className="w-full bg-indigo-600 rounded shadow-md py-1 text-white"
            onClick={e => copyCode(e, cssCode)}
          >
            Copy to Clipboard
          </button>
        </div>
      )}
      {copySuccess > 0 && (
        <span className="fixed shadow-md px-6 py-4 text-white text-xl tracking-wider bg-green-500 rounded-lg">
          Successfully Copied!
        </span>
      )}
    </section>
  );
};

export default Border;
